import { useQuery } from "@tanstack/react-query";
import { PostCard } from "@/components/feed/PostCard";
import { type HNItem } from "@/lib/hn";
import { Loader2, Trash2 } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { getSavedPosts, clearSavedPosts } from "@/lib/storage";

export function SavedPage() {
  const queryClient = useQueryClient();
  const [isClearing, setIsClearing] = useState(false);

  const { data: savedPosts, isLoading } = useQuery<HNItem[]>({
    queryKey: ["savedPosts"],
    queryFn: async () => {
      return getSavedPosts();
    },
  });

  const clearMutation = useMutation({
    mutationFn: async () => {
      clearSavedPosts();
    },
    onMutate: () => {
      setIsClearing(true);
    },
    onSettled: () => {
      setIsClearing(false);
      queryClient.invalidateQueries({ queryKey: ["savedPosts"] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-on-surface-variant">
        <Loader2 className="w-8 h-8 animate-spin mb-4 text-secondary" />
        <p className="font-label font-bold tracking-widest uppercase text-xs">Loading reading list...</p>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in duration-500 w-full flex flex-col lg:flex-row gap-8">
      {/* Feed List Container */}
      <div className="flex-1">
        <div className="flex items-center justify-between mb-8 border-b-2 border-secondary pb-4">
          <h2 className="text-3xl font-headline font-black uppercase tracking-tighter italic text-white flex items-center gap-3">
            Reading List <span className="text-sm font-sans font-bold px-3 py-1 rounded-full bg-secondary text-on-secondary ml-2 not-italic tracking-normal">{savedPosts?.length || 0}</span>
          </h2>

          {savedPosts && savedPosts.length > 0 && (
            <button
              onClick={() => clearMutation.mutate()}
              disabled={isClearing}
              className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-on-surface-variant hover:text-secondary transition-colors disabled:opacity-50 disabled:hover:text-on-surface-variant"
            >
              {isClearing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Clearing...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4" /> Clear All
                </>
              )}
            </button>
          )}
        </div>

        {savedPosts && savedPosts.length > 0 ? (
          <div className="space-y-2">
            {savedPosts.map((post: HNItem, idx: number) => (
              <PostCard
                key={post.id}
                post={post}
                rank={idx + 1}
              />
            ))}
          </div>
        ) : (
          <div className="p-16 flex flex-col items-center justify-center border border-white/20 bg-surface-container-low/20 rounded-none text-center">
            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-6">
              <span className="material-symbols-outlined text-3xl text-white/40" style={{ fontVariationSettings: "'FILL' 0" }}>bookmark</span>
            </div>
            <h3 className="text-2xl font-headline font-bold text-white mb-3 uppercase italic tracking-tight">
              Your queue is empty
            </h3>
            <p className="text-on-surface-variant text-sm font-medium mb-8 max-w-sm leading-relaxed">
              Stories you bookmark will appear here for deep reading later.
            </p>
          </div>
        )}
      </div>

      {/* Sidebar for reading list stats (Optional nice-to-have visual) */}
      <aside className="w-full lg:w-96 flex flex-col gap-10">
        <div className="bg-surface-container-high p-6 border-b-8 border-secondary hidden lg:block">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 border-2 border-secondary p-1 bg-background flex items-center justify-center">
                <span className="material-symbols-outlined text-4xl text-secondary">library_books</span>
              </div>
              <div>
                <h4 className="font-headline font-black text-2xl uppercase italic text-white leading-none">Curated</h4>
                <p className="text-xs font-black text-secondary uppercase tracking-[0.2em] mt-1">Reading Queue</p>
              </div>
            </div>
          </div>
      </aside>
    </div>
  );
}
